<script setup lang="ts">
import Typography from '@components/Typography.vue';
import Button from '@components/Button.vue';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore.ts';
import SearchOfferCard from '@pages/search/components/SearchOfferCard.vue';
import SearchListSkeleton from '@pages/search/components/SearchListSkeleton.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { computed, watch } from 'vue';
import { trans, transchoice } from '@tools/translation.ts';
import Icon from '@components/Icon.vue';
import { IconMap } from '@icons';
import SearchEmptyState from '@pages/search/components/SearchEmptyState.vue';
import type { ExtendedAlgoliaOffer } from '@global-types/algoliaOffer.ts';
import SearchReinsuranceCard from '@pages/search/components/SearchReinsuranceCard.vue';

const searchStore = useSearchStore();
const {
  offers,
  nbOffers,
  nbSpaces,
  isLoading,
  mapIsVisible,
  perPage,
} = storeToRefs(searchStore);
const { openMap, closeMap } = searchStore;

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

watch(isMobileOrTablet, () => {
  if (isMobileOrTablet.value) {
    closeMap();
  }
});

const offersToShow = computed<ExtendedAlgoliaOffer[]>(() => offers.value.slice(0, perPage.value));
</script>

<template>
  <div
    :class="[
      mapIsVisible ? 'lg:max-w-[384px] xl:max-w-[830px]' : 'max-w-screen-xl',
      'mx-auto w-full',
      'flex flex-col',
    ]"
  >
    <div
      v-show="!isLoading && nbOffers"
      class="flex items-center justify-between"
    >
      <Typography
        v-if="!isLoading"
        :variant="isMobileOrTablet ? 'small-title-tag' : 'content-heading-2'"
        color="gray-darker"
      >
        {{ transchoice('search.summary.count', { offers_count: nbOffers, spaces_count: nbSpaces }) }}
      </Typography>

      <div class="flex gap-x-2">
        <div>
          @todo Tri : Recommendations
        </div>

        <Button
          v-if="!mapIsVisible && !isMobileOrTablet"
          variant="secondary"
          :class="[
            '!h-10 !w-fit',
            '!flex items-center justify-center gap-x-2'
          ]"
          @click="openMap"
        >
          <span>
            {{ trans('global.map') }}
          </span>

          <Icon
            :svg="IconMap"
            width="24"
            height="24"
          />
        </Button>
      </div>
    </div>

    <div
      :class="[
        mapIsVisible ? 'max-w-[830px]' : 'max-w-screen-xl',
        'mx-auto lg:ml-0',
        'w-full grow',
      ]"
    >
      <SearchListSkeleton v-if="isLoading" />

      <SearchEmptyState
        v-else-if="!nbOffers"
        class="size-full"
      />

      <ul
        v-else
        :class="[
          'p-0',
          'grid grid-cols-1 gap-6',
          mapIsVisible ? 'xl:grid-cols-2' : 'min-[634px]:grid-cols-2 lg:grid-cols-3',
        ]"
      >
        <li
          v-for="offer in offersToShow.slice(0, 3)"
          :key="offer.hash_id"
          class="list-none"
        >
          <SearchOfferCard :offer="offer" />
        </li>

        <li class="list-none">
          <SearchReinsuranceCard class="h-full" />
        </li>

        <li
          v-for="offer in offersToShow.slice(3)"
          :key="offer.hash_id"
          class="list-none"
        >
          <SearchOfferCard :offer="offer" />
        </li>
      </ul>
    </div>
  </div>
</template>
